import { useCallback, useEffect, useMemo, useState } from 'preact/hooks';
import { api } from '../api/client';
import type { ExportFormat, Vocab, VocabSort } from '../api/types';
import { entryCount, t } from '../i18n';
import { describeError } from '../lib/errors';
import { formatDateTime, pageCount, truncate } from '../lib/format';
import { useDebounced, useLang } from '../lib/hooks';
import { EmptyState, ErrorBox, Field, Notice, Section, Spinner } from '../components/ui';

const PAGE_SIZE = 25;

const LANGUAGES: ReadonlyArray<readonly [string, string]> = [
  ['auto', 'Auto'],
  ['en', 'English'],
  ['ru', 'Русский'],
  ['ja', '日本語'],
  ['ko', '한국어'],
  ['zh', '中文'],
  ['es', 'Español'],
  ['de', 'Deutsch'],
  ['fr', 'Français'],
  ['it', 'Italiano'],
  ['pt', 'Português'],
  ['pl', 'Polski'],
  ['uk', 'Українська'],
  ['tr', 'Türkçe'],
  ['ar', 'العربية'],
  ['hi', 'हिन्दी'],
];

interface EditState {
  id: number;
  text: string;
  translation: string;
  note: string;
}

interface DraftState {
  text: string;
  translation: string;
  note: string;
  sourceLanguage: string;
  targetLanguage: string;
}

const EMPTY_DRAFT: DraftState = { text: '', translation: '', note: '', sourceLanguage: 'auto', targetLanguage: 'en' };

function matches(row: Vocab, needle: string, sourceLang: string, targetLang: string): boolean {
  if (sourceLang !== '' && row.source_language !== sourceLang) return false;
  if (targetLang !== '' && row.target_language !== targetLang) return false;
  if (needle === '') return true;
  const haystack = `${row.text} ${row.translation ?? ''} ${row.note ?? ''} ${row.context ?? ''}`.toLowerCase();
  return haystack.includes(needle);
}

export function VocabPage() {
  useLang();

  const [rows, setRows] = useState<Vocab[] | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [query, setQuery] = useState('');
  const [sourceLang, setSourceLang] = useState('');
  const [targetLang, setTargetLang] = useState('');
  const [sort, setSort] = useState<VocabSort>('updated_desc');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<ReadonlySet<number>>(new Set());
  const [editing, setEditing] = useState<EditState | null>(null);
  const [draft, setDraft] = useState<DraftState>(EMPTY_DRAFT);
  const [addOpen, setAddOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [version, setVersion] = useState(0);

  const debouncedQuery = useDebounced(query, 300);
  const needle = debouncedQuery.trim().toLowerCase();

  const reload = useCallback(() => setVersion((value) => value + 1), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    api
      .listVocab({
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
        q: debouncedQuery.trim() || undefined,
        sourceLanguage: sourceLang || undefined,
        targetLanguage: targetLang || undefined,
        sort,
      })
      .then((response) => {
        if (cancelled) return;
        setRows(response.items);
        setTotal(response.total);
      })
      .catch((caught: unknown) => {
        if (!cancelled) setError(describeError(caught));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [page, debouncedQuery, sourceLang, targetLang, sort, version]);

  useEffect(() => {
    if (notice === null) return;
    const timer = setTimeout(() => setNotice(null), 5000);
    return () => clearTimeout(timer);
  }, [notice]);

  const visible = useMemo(
    () => (rows ?? []).filter((row) => matches(row, needle, sourceLang, targetLang)),
    [rows, needle, sourceLang, targetLang]
  );

  const pages = pageCount(total, PAGE_SIZE);
  const allVisibleSelected = visible.length > 0 && visible.every((row) => selected.has(row.id));
  const filteringLocally = rows !== null && visible.length !== rows.length;

  const toggleRow = (id: number) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const toggleAllVisible = () => {
    const next = new Set(selected);
    if (allVisibleSelected) {
      for (const row of visible) next.delete(row.id);
    } else {
      for (const row of visible) next.add(row.id);
    }
    setSelected(next);
  };

  const startEdit = (row: Vocab) => {
    setEditing({
      id: row.id,
      text: row.text,
      translation: row.translation ?? '',
      note: row.note ?? '',
    });
    setActionError(null);
  };

  const saveEdit = async () => {
    if (editing === null || busy) return;
    // `text` has min_length=1 server-side; an emptied box would be a guaranteed 422.
    const text = editing.text.trim();
    if (text.length === 0) {
      setActionError(t('vocab.wordRequired'));
      return;
    }
    setBusy(true);
    setActionError(null);
    try {
      // Cleared optional fields become null, not "", so exports and search agree.
      const updated = await api.updateVocab(editing.id, {
        text,
        translation: editing.translation.trim() === '' ? null : editing.translation,
        note: editing.note.trim() === '' ? null : editing.note,
      });
      setRows((current) => (current === null ? current : current.map((row) => (row.id === updated.id ? updated : row))));
      setEditing(null);
      setNotice(t('vocab.updated'));
    } catch (caught) {
      setActionError(describeError(caught));
    } finally {
      setBusy(false);
    }
  };

  const deleteRow = async (row: Vocab) => {
    if (!window.confirm(t('vocab.confirmDelete', { word: row.text }))) return;
    setBusy(true);
    setActionError(null);
    try {
      await api.deleteVocab(row.id);
      setRows((current) => (current === null ? current : current.filter((item) => item.id !== row.id)));
      setSelected((current) => {
        const next = new Set(current);
        next.delete(row.id);
        return next;
      });
      setTotal((value) => Math.max(0, value - 1));
      setNotice(t('vocab.deleted'));
      if (rows !== null && rows.length === 1 && page > 0) setPage((value) => value - 1);
    } catch (caught) {
      setActionError(describeError(caught));
    } finally {
      setBusy(false);
    }
  };

  const deleteSelected = async () => {
    const ids = [...selected];
    if (ids.length === 0) return;
    if (!window.confirm(t('vocab.confirmBulk', { count: ids.length }))) return;

    setBusy(true);
    setActionError(null);
    const failures: number[] = [];

    // Sequential on purpose: the API has no bulk-delete endpoint, and hammering a
    // single-worker uvicorn with parallel deletes buys nothing.
    for (const id of ids) {
      try {
        await api.deleteVocab(id);
      } catch {
        failures.push(id);
      }
    }

    setSelected(new Set(failures));
    setBusy(false);
    if (failures.length === 0) {
      setNotice(t('vocab.deleted'));
      reload();
    } else {
      setActionError(`${t('error.unknown')} (${failures.length})`);
      reload();
    }
  };

  const createWord = async (event: Event) => {
    event.preventDefault();
    if (draft.text.trim() === '' || busy) return;
    setBusy(true);
    setActionError(null);
    try {
      await api.createVocab({
        text: draft.text.trim(),
        translation: draft.translation.trim() || null,
        note: draft.note.trim() || null,
        source_language: draft.sourceLanguage,
        target_language: draft.targetLanguage,
      });
      setDraft(EMPTY_DRAFT);
      setAddOpen(false);
      setPage(0);
      reload();
      setNotice(t('vocab.added'));
    } catch (caught) {
      setActionError(describeError(caught));
    } finally {
      setBusy(false);
    }
  };

  const importFile = async (event: Event) => {
    const input = event.currentTarget as HTMLInputElement;
    const file = input.files?.[0];
    if (file === undefined) return;

    const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
    const format: 'csv' | 'json' | 'tsv' = extension === 'json' ? 'json' : extension === 'tsv' ? 'tsv' : 'csv';

    setBusy(true);
    setActionError(null);
    try {
      const content = await file.text();
      const result = await api.importVocab({
        content,
        format,
        source_language: sourceLang || 'auto',
        target_language: targetLang || 'en',
        skip_duplicates: true,
      });
      setNotice(
        `${t('vocab.importResult', { imported: result.imported, skipped: result.skipped })}${
          result.errors.length > 0 ? ` · ${t('vocab.importErrors', { count: result.errors.length })}` : ''
        }`
      );
      setPage(0);
      reload();
    } catch (caught) {
      setActionError(describeError(caught));
    } finally {
      input.value = '';
      setBusy(false);
    }
  };

  const exportAs = async (format: ExportFormat) => {
    if (total === 0) {
      setNotice(t('vocab.exportEmpty'));
      return;
    }
    setBusy(true);
    setActionError(null);
    try {
      const filename = await api.downloadExport(format);
      setNotice(`${t('vocab.export')}: ${filename}`);
    } catch (caught) {
      setActionError(describeError(caught));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div class="page">
      <header class="page__header">
        <div>
          <h1>{t('vocab.title')}</h1>
          <p class="muted">{t('vocab.subtitle')}</p>
        </div>
        <div class="page__actions">
          <button type="button" class="btn btn--primary" onClick={() => setAddOpen((open) => !open)}>
            {t('vocab.add')}
          </button>
          <button type="button" class="btn" disabled={busy} onClick={() => void exportAs('csv')}>
            CSV
          </button>
          <button type="button" class="btn" disabled={busy} onClick={() => void exportAs('tsv')}>
            TSV
          </button>
          <button type="button" class="btn" disabled={busy} onClick={() => void exportAs('json')}>
            JSON
          </button>
        </div>
      </header>

      {notice ? <Notice tone="ok">{notice}</Notice> : null}
      {actionError ? <ErrorBox message={actionError} /> : null}

      {addOpen ? (
        <Section title={t('vocab.addTitle')}>
          <form class="form form--inline" onSubmit={createWord}>
            <Field label={t('vocab.fieldWord')} htmlFor="new-word">
              <input
                id="new-word"
                type="text"
                required
                value={draft.text}
                onInput={(event) => setDraft({ ...draft, text: event.currentTarget.value })}
              />
            </Field>
            <Field label={t('vocab.fieldTranslation')} htmlFor="new-translation">
              <input
                id="new-translation"
                type="text"
                value={draft.translation}
                onInput={(event) => setDraft({ ...draft, translation: event.currentTarget.value })}
              />
            </Field>
            <Field label={t('vocab.filterSource')} htmlFor="new-source">
              <select
                id="new-source"
                value={draft.sourceLanguage}
                onChange={(event) => setDraft({ ...draft, sourceLanguage: event.currentTarget.value })}
              >
                {LANGUAGES.map(([code, label]) => (
                  <option key={code} value={code}>
                    {label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label={t('vocab.filterTarget')} htmlFor="new-target">
              <select
                id="new-target"
                value={draft.targetLanguage}
                onChange={(event) => setDraft({ ...draft, targetLanguage: event.currentTarget.value })}
              >
                {LANGUAGES.filter(([code]) => code !== 'auto').map(([code, label]) => (
                  <option key={code} value={code}>
                    {label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label={t('vocab.fieldNote')} htmlFor="new-note">
              <input
                id="new-note"
                type="text"
                value={draft.note}
                onInput={(event) => setDraft({ ...draft, note: event.currentTarget.value })}
              />
            </Field>
            <div class="form__actions">
              <button type="submit" class="btn btn--primary" disabled={busy || draft.text.trim() === ''}>
                {t('common.save')}
              </button>
              <button type="button" class="btn btn--ghost" onClick={() => setAddOpen(false)}>
                {t('common.cancel')}
              </button>
            </div>
          </form>
        </Section>
      ) : null}

      <Section title={t('vocab.title')} description={entryCount(total)}>
        <div class="toolbar">
          <Field label={t('vocab.search')} hint={t('vocab.searchHint')} htmlFor="vocab-search">
            <input
              id="vocab-search"
              type="search"
              value={query}
              placeholder={t('vocab.search')}
              onInput={(event) => {
                setQuery(event.currentTarget.value);
                setPage(0);
              }}
            />
          </Field>
          <Field label={t('vocab.filterSource')} htmlFor="filter-source">
            <select
              id="filter-source"
              value={sourceLang}
              onChange={(event) => {
                setSourceLang(event.currentTarget.value);
                setPage(0);
              }}
            >
              <option value="">{t('vocab.filterAll')}</option>
              {LANGUAGES.map(([code, label]) => (
                <option key={code} value={code}>
                  {label}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t('vocab.filterTarget')} htmlFor="filter-target">
            <select
              id="filter-target"
              value={targetLang}
              onChange={(event) => {
                setTargetLang(event.currentTarget.value);
                setPage(0);
              }}
            >
              <option value="">{t('vocab.filterAll')}</option>
              {LANGUAGES.filter(([code]) => code !== 'auto').map(([code, label]) => (
                <option key={code} value={code}>
                  {label}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t('vocab.sort')} htmlFor="vocab-sort">
            <select
              id="vocab-sort"
              value={sort}
              onChange={(event) => {
                setSort(event.currentTarget.value as VocabSort);
                setPage(0);
              }}
            >
              <option value="updated_desc">{t('vocab.sortUpdatedDesc')}</option>
              <option value="created_desc">{t('vocab.sortCreatedDesc')}</option>
              <option value="created_asc">{t('vocab.sortCreatedAsc')}</option>
              <option value="alpha">{t('vocab.sortAlpha')}</option>
            </select>
          </Field>
          <label class="btn btn--ghost file-button">
            {t('vocab.import')}
            <input type="file" accept=".csv,.tsv,.json,text/csv,application/json" onChange={importFile} disabled={busy} />
          </label>
        </div>
        <p class="muted small">{t('vocab.importHint')}</p>

        {selected.size > 0 ? (
          <div class="selection-bar">
            <span>{t('vocab.selected', { count: selected.size })}</span>
            <button type="button" class="btn btn--danger" disabled={busy} onClick={() => void deleteSelected()}>
              {t('vocab.bulkDelete')}
            </button>
            <button type="button" class="btn btn--ghost" onClick={() => setSelected(new Set())}>
              {t('vocab.clearSelection')}
            </button>
          </div>
        ) : null}

        {loading && rows === null ? <Spinner /> : null}
        {error !== null ? <ErrorBox message={error} onRetry={reload} /> : null}

        {!loading && error === null && rows !== null && rows.length === 0 ? (
          <EmptyState title={t('vocab.empty')} hint={t('vocab.emptyHint')} />
        ) : null}

        {rows !== null && rows.length > 0 && visible.length === 0 ? (
          <EmptyState title={t('vocab.noMatches')} hint={filteringLocally ? t('vocab.searchHint') : undefined} />
        ) : null}

        {visible.length > 0 ? (
          <div class="table-wrap">
            <table class="table">
              <thead>
                <tr>
                  <th scope="col" class="table__check">
                    <input
                      type="checkbox"
                      checked={allVisibleSelected}
                      aria-label={t('vocab.selectAll')}
                      onChange={toggleAllVisible}
                    />
                  </th>
                  <th scope="col">{t('vocab.colWord')}</th>
                  <th scope="col">{t('vocab.colTranslation')}</th>
                  <th scope="col">{t('vocab.colLangs')}</th>
                  <th scope="col">{t('vocab.colContext')}</th>
                  <th scope="col">{t('vocab.colNote')}</th>
                  <th scope="col">{t('vocab.colUpdated')}</th>
                  <th scope="col">{t('vocab.colActions')}</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((row) => {
                  const isEditing = editing !== null && editing.id === row.id;
                  return (
                    <tr key={row.id} class={isEditing ? 'is-editing' : undefined}>
                      <td class="table__check">
                        <input
                          type="checkbox"
                          checked={selected.has(row.id)}
                          aria-label={row.text}
                          onChange={() => toggleRow(row.id)}
                        />
                      </td>
                      {isEditing && editing !== null ? (
                        <>
                          <td>
                            <input
                              type="text"
                              value={editing.text}
                              aria-label={t('vocab.fieldWord')}
                              onInput={(event) => setEditing({ ...editing, text: event.currentTarget.value })}
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              value={editing.translation}
                              aria-label={t('vocab.fieldTranslation')}
                              onInput={(event) => setEditing({ ...editing, translation: event.currentTarget.value })}
                            />
                          </td>
                          <td class="nowrap">
                            {row.source_language} → {row.target_language}
                          </td>
                          <td title={row.context ?? ''}>{truncate(row.context, 60)}</td>
                          <td>
                            <input
                              type="text"
                              value={editing.note}
                              aria-label={t('vocab.fieldNote')}
                              onInput={(event) => setEditing({ ...editing, note: event.currentTarget.value })}
                            />
                          </td>
                          <td class="nowrap">{formatDateTime(row.updated_at)}</td>
                          <td class="nowrap">
                            <button type="button" class="btn btn--primary btn--sm" disabled={busy} onClick={() => void saveEdit()}>
                              {busy ? t('common.saving') : t('common.save')}
                            </button>
                            <button type="button" class="btn btn--ghost btn--sm" onClick={() => setEditing(null)}>
                              {t('common.cancel')}
                            </button>
                          </td>
                        </>
                      ) : (
                        <>
                          <td>
                            <span class="word">{row.text}</span>
                            {row.occurrence_count > 1 ? (
                              <span class="muted small"> · {t('vocab.occurrences', { count: row.occurrence_count })}</span>
                            ) : null}
                          </td>
                          <td title={row.translation ?? ''}>{truncate(row.translation, 60)}</td>
                          <td class="nowrap">
                            {row.source_language} → {row.target_language}
                          </td>
                          <td title={row.context ?? ''}>{truncate(row.context, 60)}</td>
                          <td title={row.note ?? ''}>{truncate(row.note, 40)}</td>
                          <td class="nowrap">{formatDateTime(row.updated_at)}</td>
                          <td class="nowrap">
                            <button type="button" class="btn btn--ghost btn--sm" onClick={() => startEdit(row)}>
                              {t('common.edit')}
                            </button>
                            <button
                              type="button"
                              class="btn btn--danger btn--sm"
                              disabled={busy}
                              onClick={() => void deleteRow(row)}
                            >
                              {t('common.delete')}
                            </button>
                          </td>
                        </>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : null}

        <nav class="pager" aria-label={t('vocab.title')}>
          <button type="button" class="btn" disabled={page === 0 || loading} onClick={() => setPage((value) => Math.max(0, value - 1))}>
            {t('vocab.prev')}
          </button>
          <span class="muted">{t('vocab.page', { page: page + 1, pages })}</span>
          <button
            type="button"
            class="btn"
            disabled={page + 1 >= pages || loading}
            onClick={() => setPage((value) => value + 1)}
          >
            {t('vocab.next')}
          </button>
        </nav>
      </Section>
    </div>
  );
}
